import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import SoftBox from "components/SoftBox";
import { useEffect, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Form, Formik } from "formik";
import { connectorVersionValidation } from "./schemas/validations";
import { version } from "./schemas/version";
import { styled } from "@mui/material/styles";
import { THEME_COLORS } from "constants/theme";
import FormField from "layouts/Flow Designer/components/FormField";
import { connectorVersionInitialValues } from "./schemas/initialValues";
import { uploadConnectorZipFile, extractConnectorZipFileInformation } from "api/ComponentsApi";
import { insertConnectorVersion, updateConnectorVersion } from "api/ConnectorVersions";
import { CircularProgress, Icon, IconButton } from "@mui/material";
import { toast } from "react-toastify";
import JSZip from "jszip";
import { CONNECTOR_REQUIRED_FILES } from "constants/flowDesigner";
import { updateComponent } from "redux/actions/flowDesigner/cxfComponentsActionCreator";
import { useDispatch } from "react-redux";
import { ThemedXIcon } from "assets/CXFIcons/Static";
import { isValidUuid } from "helpers/versions";

const FileNamesWrapper = styled(SoftTypography)(({ theme }) => ({
   position: "absolute",
   top: "40px",
   left: "110px",
   lineHeight: "20px",
   minWidth: "150px",
   backgroundColor: THEME_COLORS[theme.palette?.mode?.toUpperCase()].INPUT_BG
}));

export const NewVersionDialog = ({ openNewVersionDialog, onClose, handleAppendVersion, handleUpdateVersion, connector, connectorVersions }) => {
   const formRef = useRef(null);
   const { user } = useAuth0();
   const [isCreatingVersion, setIsCreatingVersion] = useState(false);
   const [error, setError] = useState("");
   const [selectedConfigFile, setSelectedConfigFile] = useState(null);
   const [selectedFileNames, setSelectedFileNames] = useState("");
   const dispatch = useDispatch();

   const [showConfirmReplace, setShowConfirmReplace] = useState(false);
   const [existingVersionRecord, setExistingVersionRecord] = useState(null);
   const [isReplacing, setIsReplacing] = useState(false);
   const [pendingValues, setPendingValues] = useState(null);

   const {
      formId,
      formField: { configFile }
   } = version;

   const handleFormSubmit = async (values) => {
      if (!user?.stripeId || isCreatingVersion) return;
      setError("");

      try {
         setIsCreatingVersion(true);
         const configFilesUpload = document.getElementById("componentConfigUpload");
         const file = selectedConfigFile || configFilesUpload?.files?.[0];

         if (!file) {
            setError("Config file is required.");
            setIsCreatingVersion(false);
            return;
         }

         const zipResponse = await extractConnectorZipFileInformation(file);

         if (!zipResponse?.isSuccessful) {
            setError(zipResponse?.message || "Failed to extract connector information");
            toast.error(zipResponse?.message || "Failed to extract connector information", {
               autoClose: 5000,
               pauseOnHover: true
            });
            setIsCreatingVersion(false);
            return;
         }

         const extractedSchema = zipResponse?.resultObject?.schema;

         const connectorSchema = connector?.schema;

         const readmeContent = zipResponse?.resultObject?.readme || "";
         const updateInput = {
            documentation: readmeContent
         };

          if (!isValidUuid(extractedSchema.componentUuid)) {
            setError(`Component UUID in schema.json is missing or invalid. Expecting "${connector.id}".`);
            setIsCreatingVersion(false);
            return;
         }

          if (extractedSchema.componentUuid !== connector.id) {
             setError(`Component UUID mismatch. Expected "${connector.id}" but got "${extractedSchema.componentUuid}"`);
             setIsCreatingVersion(false);
             return;
          }

         if (connectorSchema) {
            if (!extractedSchema?.componentId) {
               setError("Component ID is missing in schema.json");
               setIsCreatingVersion(false);
               return;
            }

            if (extractedSchema.componentId !== connectorSchema.componentId) {
               setError(`Component ID mismatch. Expected "${connectorSchema.componentId}" but got "${extractedSchema.componentId}"`);
               setIsCreatingVersion(false);
               return;
            }

            const extractedDeveloper = extractedSchema?.developer;
            if (extractedDeveloper && extractedDeveloper !== connectorSchema.developer) {
               updateInput.schema = {
                  componentId: connectorSchema.componentId,
                  developer: extractedDeveloper
               };
            }
         } else if (extractedSchema) {
            updateInput.schema = {
               componentId: extractedSchema.componentId || "",
               developer: extractedSchema.developer || ""
            };
         }

         const extractedVersion = extractedSchema?.version;
         if (!extractedVersion) {
            setError("Version is missing in schema.json");
            setIsCreatingVersion(false);
            return;
         }

         const existingRecord = connectorVersions?.find((v) => v.version === extractedVersion);
         if (existingRecord) {
            setExistingVersionRecord(existingRecord);
            setPendingValues({ file, releaseNotes: values.releaseNotes, updateInput });
            setShowConfirmReplace(true);
            setIsCreatingVersion(false);
            return;
         }

         const connectorZipResult = await uploadConnectorZipFile(file, user.stripeId, connector.id, `${extractedVersion}`);

         if (!connectorZipResult?.isSuccessful) {
            toast.error(connectorZipResult?.message, {
               autoClose: 5000,
               pauseOnHover: true
            });

            setIsCreatingVersion(false);

            return;
         }

         const zipResultObject = connectorZipResult?.resultObject;

         const config_files = zipResultObject?.uploadResults?.map((r) => r.Location).join(",");

         const input = {
            tenant_id: user.stripeId,
            connector_id: connector.id,
            config_files: config_files,
            filename: zipResultObject?.fileName ?? "",
            filesize: zipResultObject?.fileSize ?? 0,
            version: extractedVersion,
            release_notes: values.releaseNotes
         };

         const connectorVersion = await insertConnectorVersion(...Object.values(input));
         if (!connectorVersion.success || !connectorVersion.data?.isSuccessful || !connectorVersion.data?.resultObject?.id) {
            setError(connectorVersion.message);
            
            setIsCreatingVersion(false);
            
            return;
         }

         dispatch(
            updateComponent(
               user,
               connector.id,
               updateInput,
               false
            )
         );

         input.id = connectorVersion.data.resultObject.id;
         input.file_name = input.filename;
         input.file_size = input.filesize;
         input.created_datetime = new Date().toISOString();
         input.updated_datetime = input.created_datetime;
         handleAppendVersion(input);

         toast.success("Successfully created new version");
      } catch (error) {
         setError(`Unable to create new connector version: ${error.message}`);
         
         setIsCreatingVersion(false);
         
         return;
      }

      setIsCreatingVersion(false);

      onClose();
   };

   const handleConfirmReplace = async () => {
      if (isReplacing || !existingVersionRecord || !pendingValues) return;
      setIsReplacing(true);
      try {
         const { file, releaseNotes, updateInput } = pendingValues;
         const connectorZipResult = await uploadConnectorZipFile(
            file,
            user.stripeId,
            connector.id,
            existingVersionRecord.version
         );

         if (!connectorZipResult?.isSuccessful) {
            toast.error(connectorZipResult?.message, {
               autoClose: 5000,
               pauseOnHover: true
            });
            setIsReplacing(false);
            return;
         }

         const newZipUrl = connectorZipResult.resultObject?.uploadResults?.find((uploadResult) => {
            const uploadedFileUrl = uploadResult.Location || uploadResult.url;
            return uploadedFileUrl && decodeURIComponent(new URL(uploadedFileUrl).pathname.split("/").pop()) === connectorZipResult.resultObject?.fileName;
         });

         const uploadedZipUrl = newZipUrl?.Location || newZipUrl?.url;
         if (!uploadedZipUrl) {
            toast.error("The uploaded connector ZIP file could not be located.");
            return;
         }

         const updatedConfigFiles = connectorZipResult.resultObject.uploadResults
            .map((result) => result.Location || result.url)
            .filter(Boolean)
            .join(",");

         const newFileName = connectorZipResult.resultObject?.fileName || file.name;
         const newFileSize = connectorZipResult.resultObject?.fileSize || file.size;

         const updatedVersion = await updateConnectorVersion(
            {
               tenant_id: existingVersionRecord.tenant_id,
               connector_id: existingVersionRecord.connector_id,
               config_files: updatedConfigFiles,
               filename: newFileName,
               filesize: newFileSize,
               version: existingVersionRecord.version,
               release_notes: releaseNotes || existingVersionRecord.release_notes
            },
            existingVersionRecord.id
         );

         if (updatedVersion.success) {
            dispatch(
               updateComponent(
                  user,
                  connector.id,
                  updateInput,
                  false
               )
            );

            handleUpdateVersion({
               id: existingVersionRecord.id,
               config_files: updatedConfigFiles,
               file_name: newFileName,
               file_size: newFileSize,
               release_notes: releaseNotes || existingVersionRecord.release_notes,
               ...updatedVersion.data
            });

            toast.success("Successfully replaced version");
            onClose();
         } else setError(updatedVersion.message);
      } catch (error) {
         toast.error("Something went wrong, please try again");
      }
      setIsReplacing(false);
      setShowConfirmReplace(false);
      setExistingVersionRecord(null);
      setPendingValues(null);
   };

   const handleCancelReplace = () => {
      setShowConfirmReplace(false);
      setExistingVersionRecord(null);
      setPendingValues(null);
   };

   const resetForm = () => {
      formRef.current?.resetForm();
      setError("");
      setSelectedConfigFile(null);
      setSelectedFileNames("");
      setShowConfirmReplace(false);
      setExistingVersionRecord(null);
      setPendingValues(null);
   };

   const validateConfigFiles = async (value, uploaded) => {
      if (!value) {
         return "Config file is required.";
      }

      const file = uploaded?.files?.[0] || value;

      try {
         const zip = await JSZip.loadAsync(file);

         const entries = Object.keys(zip.files);

         if (entries.some((name) => name.includes("/"))) {
            return "ZIP must not contain folders or nested files.";
         }

         const topLevelFiles = entries.filter((name) => !name.includes("/"));

         const missingFiles = CONNECTOR_REQUIRED_FILES.filter((req) => !topLevelFiles.includes(req));
         if (missingFiles.length > 0) {
            return `Missing required files: ${missingFiles.join(", ")}`;
         }

         return null;
      } catch (e) {
         return "Invalid ZIP file.";
      }
   };

   useEffect(() => {
      if (!openNewVersionDialog) return;

      setTimeout(() => {
         const configFilesUpload = document.getElementById("componentConfigUpload");

         if (configFilesUpload) {
            configFilesUpload.setAttribute("accept", ".zip");
         }
      }, 500);

      resetForm();
   }, [openNewVersionDialog]);

   return (
      <>
         <Dialog fullWidth maxWidth="md" open={openNewVersionDialog} onClose={onClose}>
            <DialogContent>
               <DialogTitle>Add Version</DialogTitle>
               <Formik validationSchema={connectorVersionValidation} onSubmit={handleFormSubmit} innerRef={formRef} enableReinitialize initialValues={connectorVersionInitialValues}>
                  {({ errors, touched, isValid, setFieldTouched, setFieldValue }) => {
                     const uploaded = document.getElementById("componentConfigUpload");

                     return (
                        <Form id={formId} autoComplete="off">
                           <SoftBox
                              sx={{
                                 padding: "0 16px"
                              }}
                           >
                              <SoftBox className="border-top" sx={{ marginBottom: "20px" }} />
                              <SoftBox sx={{ position: "relative" }}>
                                 <FormField
                                    id="componentConfigUpload"
                                    type={configFile.type}
                                    label={configFile.label}
                                    name={configFile.name}
                                    error={errors[configFile.name] && touched[configFile.name]}
                                    placeholder={configFile.placeholder ? configFile.placeholder : ""}
                                    required
                                    onChange={async (event) => {
                                       const file = event.target.files?.[0] || null;
                                       const fileNames = event.target.files?.length
                                          ? Array.from(event.target.files)
                                             .map((selectedFile) => selectedFile.name)
                                             .join(", ")
                                          : "";
                                       const validationError = await validateConfigFiles(file, event.target);

                                       setFieldValue(configFile.name, file);
                                       setFieldTouched(configFile.name, true, false);
                                       setSelectedFileNames(fileNames);
                                       setSelectedConfigFile(validationError ? null : file);
                                       setError(validationError || "");
                                    }}
                                    validate={async (value) => await validateConfigFiles(value, uploaded)}
                                 />
                                 {selectedFileNames && (
                                    <FileNamesWrapper component="div" variant="caption" color="dark" pl={0.5} pr={4} sx={{ left: "106px" }}>
                                       {selectedFileNames}
                                    </FileNamesWrapper>
                                 )}
                                 {error && (
                                    <SoftTypography variant="caption" color="error" sx={{ display: "block", mt: 1, fontSize: "14px" }}>
                                       {error}
                                    </SoftTypography>
                                 )}
                              </SoftBox>
                           </SoftBox>
                           <DialogActions>
                              <SoftButton variant="outlined" color="info" onClick={onClose}>
                                 Close
                              </SoftButton>
                              <SoftButton color="primary" onClick={() => formRef.current.submitForm()} disabled={!isValid || isCreatingVersion || !selectedConfigFile || Boolean(error)}>
                                 {isCreatingVersion ? (
                                    <CircularProgress
                                       size={24}
                                       sx={{
                                          color: "#fff",
                                          position: "absolute",
                                          top: "50%",
                                          left: "50%",
                                          marginTop: "-12px",
                                          marginLeft: "-12px",
                                          zIndex: 1
                                       }}
                                    />
                                 ) : (
                                    "Add"
                                 )}
                              </SoftButton>
                           </DialogActions>
                        </Form>
                     );
                  }}
               </Formik>
            </DialogContent>
         </Dialog>

         <Dialog open={showConfirmReplace} onClose={handleCancelReplace} fullWidth maxWidth="sm">
            <DialogTitle
               sx={{
                  marginBottom: "20px",
                  padding: "32px 24px 0 24px !important",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center"
               }}
               className="white-to-dark"
            >
               <SoftBox display="flex" alignItems="center">
                  <Icon sx={{ fontSize: "18px !important", marginRight: "16px" }}>warning</Icon>
                  Replace version
               </SoftBox>
               <IconButton onClick={handleCancelReplace}>
                  <ThemedXIcon />
               </IconButton>
            </DialogTitle>
            <DialogContent sx={{ padding: "0 32px 24px" }}>
               <SoftBox className="border-top" sx={{ paddingTop: "20px" }}>
                  <SoftTypography color="text" sx={{ fontSize: "16px", marginBottom: "24px" }}>
                     Version <strong>{existingVersionRecord?.version}</strong> already exists. Are you sure you want to replace it?
                  </SoftTypography>
                  <SoftBox sx={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
                     <SoftButton variant="outlined" color="info" onClick={handleCancelReplace} disabled={isReplacing}>
                        Cancel
                     </SoftButton>
                     <SoftButton color="primary" onClick={handleConfirmReplace} disabled={isReplacing}>
                        {isReplacing ? <CircularProgress size={24} sx={{ color: "#fff" }} /> : "Replace"}
                     </SoftButton>
                  </SoftBox>
               </SoftBox>
            </DialogContent>
         </Dialog>
      </>
   );
};
